import styled from 'styled-components';

// `theme.colors.accent.light` doesn't exist on any theme (client/partner/
// white-label) - it always fell back to the hardcoded '#d7ccee' below. Keep
// that exact default, but derive a matching tint from brand primary once a
// white-label theme with real color overrides is active (a theme with only
// a name/logo has no custom colors to derive from - see
// whiteLabel.hasCustomColors in src/styles/mergeWhiteLabelTheme.js).
const potAccentColor = (theme) =>
  theme?.whiteLabel?.hasCustomColors
    ? `color-mix(in srgb, ${theme.colors.brand.primary} 25%, white)`
    : '#d7ccee';

export const ProgressContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 2px solid ${(props) => potAccentColor(props.theme)};
  background: white;
  overflow: hidden !important;
  /* iOS Safari specific overflow fix */
  -webkit-mask: radial-gradient(circle, white 100%, transparent 100%);
  mask: radial-gradient(circle, white 100%, transparent 100%);

  &::before {
    content: '';
    display: block;
    position: absolute;
    top: 50%;
    left: 50%;
    width: 50%;
    height: 50%;
    border-radius: 50%;
    min-width: 8px;
    min-height: 8px;
    transform: translate(-50%, -50%);
  }

  &[data-value='100'] {
    border-color: ${(props) => props.theme?.colors?.brand?.primary || '#6725f4ad'};

    @keyframes fireworkAnimation {
      from,
      0% {
        width: 50%;
        height: 50%;
        background: ${(props) => props.theme?.colors?.brand?.secondary || '#967dcc'};
        opacity: 1;
      }
      25% {
        opacity: 1;
      }
      to,
      100% {
        width: 150%;
        height: 150%;
        background: ${(props) => potAccentColor(props.theme)};
        opacity: 0;
      }
    }

    &::before {
      animation: fireworkAnimation 1.3s 0.1s ease infinite both;
    }
  }
`;

export const ProgressInner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: transparent;
  position: relative;

  overflow: hidden !important;
  /* Additional iOS Safari overflow containment */
  -webkit-mask: radial-gradient(circle, white 100%, transparent 100%);
  mask: radial-gradient(circle, white 100%, transparent 100%);
  /* Ensure proper stacking context on iOS */
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
`;

export const ProgressIndicator = styled.div`
  position: absolute;
  top: ${({value}) => `calc(100% - ${value}%)`};
  left: -50%;
  right: 50%;
  width: 200%;
  height: 200%;
  border-radius: 35%;
  opacity: 0.6;
  background: ${(props) => potAccentColor(props.theme)};
  transform-origin: center center;
  /* Ensure proper clipping on iOS */
  will-change: transform;
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;

  @keyframes primaryWaveAnimation {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
  animation: primaryWaveAnimation 8s linear infinite;
`;

export const ProgressIndicatorGrey = styled.div`
  position: absolute;
  top: ${({value}) => `calc(102% - ${value}%)`}; /* Slight offset for better visibility */
  left: -50%;
  right: 50%;
  width: 200%;
  height: 200%;
  transform-origin: center center;
  background: ${(props) => potAccentColor(props.theme)};
  transform: rotate(180deg);
  border-radius: 35%;
  /* Ensure proper clipping on iOS */
  will-change: transform;
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;

  @keyframes secondaryWaveAnimation {
    from {
      transform: rotate(180deg);
    }
    to {
      transform: rotate(540deg);
    }
  }
  animation: secondaryWaveAnimation 12s linear infinite;
`;

// Muted label color, tinted by brand primary for a white-label theme with
// real color overrides so it doesn't stay purple-grey regardless of the
// active brand - see whiteLabel.hasCustomColors in mergeWhiteLabelTheme.js.
const potTextColor = (theme) =>
  theme?.whiteLabel?.hasCustomColors
    ? `color-mix(in srgb, ${theme.colors.brand.primary} 45%, ${theme.colors.text.secondary})`
    : '#71678e';

export const TextPercentage = styled.p`
  color: ${(props) => potTextColor(props.theme)} !important;
  font-size: 1.35rem;
  margin: 0;
  position: absolute;
  z-index: 2;
  font-weight: 600;
  text-align: center;
  height: max-content;
  width: 100%;

  display: flex;
  justify-content: center;
  align-items: center;
`;

export const Wrapper = styled.div`
  height: ${(props) => `${props.size}px`};
  width: ${(props) => `${props.size}px`};
`;
